import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { DollarSign, Save, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DriverExpenseEntry = () => {
  const [isOutstation, setIsOutstation] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    day_start_reading: '',
    day_end_reading: '',
    cng: '',
    petrol: '',
    subscription: '',
    digital_payment: '',
    uber_wallet: '',
    ola_earning: '',
    rapido_earning: '',
    namma_yatri_earning: '',
    cash_collected: '',
    description: '',
    driver_name: '',
    driver_phone: '',
    // Outstation specific fields
    trip_type: 'regular',
    start_date: '',
    end_date: '',
    toll_start_balance: '',
    toll_end_balance: '',
    personal_expense: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotalKms = () => {
    const start = parseFloat(formData.day_start_reading) || 0;
    const end = parseFloat(formData.day_end_reading) || 0;
    return end > start ? (end - start).toFixed(2) : 0;
  };

  const calculateTollExpense = () => {
    const start = parseFloat(formData.toll_start_balance) || 0;
    const end = parseFloat(formData.toll_end_balance) || 0;
    return start > end ? (start - end).toFixed(2) : 0;
  };

  const handleToggleChange = () => {
    setIsOutstation(!isOutstation);
    // Update trip_type in formData
    setFormData(prev => ({
      ...prev,
      trip_type: !isOutstation ? 'outstation' : 'regular',
      // Reset outstation-specific fields when switching
      start_date: !isOutstation ? prev.date : '',
      end_date: !isOutstation ? prev.date : '',
      toll_start_balance: '',
      toll_end_balance: '',
      personal_expense: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - only driver name is mandatory
    if (!formData.driver_name) {
      toast.error('Please provide your name');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare data with calculated total
      const submitData = {
        ...formData,
        total_kms: calculateTotalKms()
      };

      // Convert empty strings to null for numeric fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      await axios.post(`${BACKEND_URL}/api/expenses`, submitData);
      
      toast.success('✅ Expense entry submitted successfully! Redirecting to homepage...', {
        duration: 10000
      });
      
      // Redirect to homepage after 10 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 10000);
      
      // Reset form but keep driver info and trip type
      const driverInfo = {
        driver_name: formData.driver_name,
        driver_phone: formData.driver_phone,
        trip_type: formData.trip_type
      };
      
      setFormData({
        date: new Date().toISOString().split('T')[0],
        day_start_reading: '',
        day_end_reading: '',
        cng: '',
        petrol: '',
        subscription: '',
        digital_payment: '',
        uber_wallet: '',
        ola_earning: '',
        rapido_earning: '',
        namma_yatri_earning: '',
        cash_collected: '',
        description: '',
        start_date: '',
        end_date: '',
        toll_start_balance: '',
        toll_end_balance: '',
        personal_expense: '',
        ...driverInfo
      });
    } catch (error) {
      console.error('Error submitting expense:', error);
      toast.error('Failed to submit expense entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/sg-cabs-logo.png" 
              alt="SG Cabs" 
              className="h-16 w-16 mr-3"
            />
            <h1 className="text-4xl font-bold text-gray-900">SG Cabs</h1>
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Driver Expense Entry</h2>
          <p className="text-gray-600">Submit your daily expenses and earnings</p>
        </div>

        <Card className="shadow-xl border-2 border-gray-200">
          <CardHeader className="bg-gradient-to-r from-black to-gray-800 text-white">
            <CardTitle className="text-2xl flex items-center">
              <DollarSign className="w-8 h-8 mr-3 text-yellow-400" />
              Daily Expense Report
            </CardTitle>
          </CardHeader>

          <CardContent className="p-8">
            {/* Trip Type Toggle */}
            <div className="mb-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isOutstation ? 'bg-orange-500' : 'bg-gray-400'
                  }`}>
                    <span className="text-2xl">🚗</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {isOutstation ? 'Outstation Trip' : 'Regular Trip'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {isOutstation 
                        ? 'Multi-day outstation trip with toll expenses' 
                        : 'Regular daily city trip'}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isOutstation}
                    onChange={handleToggleChange}
                    className="sr-only peer"
                  />
                  <div className="w-16 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-8 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Driver Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Driver Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="driver_name"
                      value={formData.driver_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="driver_phone"
                      value={formData.driver_phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="Enter phone number (optional)"
                    />
                  </div>
                </div>
              </div>

              {/* Date - Regular Trip */}
              {!isOutstation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Date</h3>
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-6 h-6 text-gray-600" />
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Date Range - Outstation Trip */}
              {isOutstation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Trip Duration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date *
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Odometer Readings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Odometer Readings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day Start Reading (km)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="day_start_reading"
                      value={formData.day_start_reading}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day End Reading (km)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="day_end_reading"
                      value={formData.day_end_reading}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                {formData.day_start_reading && formData.day_end_reading && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Total Distance:</span> {calculateTotalKms()} km
                    </p>
                  </div>
                )}
              </div>

              {/* Expenses */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Expenses</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CNG
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                        ₹
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        name="cng"
                        value={formData.cng}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Petrol
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                        ₹
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        name="petrol"
                        value={formData.petrol}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  {!isOutstation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subscription
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="subscription"
                          value={formData.subscription}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Personal Expense
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                        ₹
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        name="personal_expense"
                        value={formData.personal_expense}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Toll Expense - Outstation Only */}
              {isOutstation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Toll Expenses</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Toll Starting Balance
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="toll_start_balance"
                          value={formData.toll_start_balance}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Toll End Balance
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="toll_end_balance"
                          value={formData.toll_end_balance}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  {formData.toll_start_balance && formData.toll_end_balance && (
                    <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-sm text-orange-900">
                        <span className="font-semibold">Toll Expense:</span> ₹{calculateTollExpense()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Earnings - Regular Trip Only */}
              {!isOutstation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Earnings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Digital Payment
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="digital_payment"
                          value={formData.digital_payment}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Uber Wallet
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="uber_wallet"
                          value={formData.uber_wallet}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ola Earning
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="ola_earning"
                          value={formData.ola_earning}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rapido Earning
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="rapido_earning"
                          value={formData.rapido_earning}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cash Collected
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                          ₹
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          name="cash_collected"
                          value={formData.cash_collected}
                          onChange={handleChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Total Earning - Outstation */}
              {isOutstation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Total Trip Earning</h3>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-semibold text-lg">
                      ₹
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      name="cash_collected"
                      value={formData.cash_collected}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Total amount collected from outstation trip</p>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes / Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Any additional notes or comments..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`inline-flex items-center px-8 py-4 bg-black hover:bg-gray-900 text-yellow-400 rounded-lg font-bold text-lg transition-colors shadow-lg ${
                    submitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-400 mr-3"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5 mr-3" />
                      Submit Expense Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-600">
          <p className="text-sm">© 2026 SG Cabs - Driver Expense Tracking</p>
        </div>
      </div>
    </div>
  );
};
